import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {isMobile} from 'react-device-detect';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import TextInput from 'app/Components/Shared/TextInput/TextInput';
import ReactSelect from 'app/Components/Shared/ReactSelect/ReactSelect';
import 'app/Components/Shared/ReactSelect/ReactSelect.scss';
import useEnterKeyPress from '../hooks/useEnterKeyPress';
import {useBankBranchSelection} from '../hooks/useBankBranchSelection';
import {useSelectedBansForUpdatePayment} from '../hooks/useSelectedBansForUpdatePayment';
import UpdatePaymentBanStrip from '../SharedComponents/UpdatePaymentBanStrip';
import BranchAutocompleteInput from '../SharedComponents/BranchAutocompleteInput';
import {popupEnum} from '../Constants/debtPaymentEnum';
import {validateInput, TextInputEnum} from '../../../../utilities/validation/InputValidation';
import {selectServerData, selectEpiData} from '../DebtAndUpdatePaymentBlockSlice';
import {dwhUpdatePaymentMethodReport, DwhActionType, DWHMessageText, DWHStatus} from '../reports/dwhReports';

const STEP_TITLE = 'הוראת קבע דרך חשבון בנק';

const BankBranchSelection = () => {
  const {
    selectedBanData,
    bankObligationInfo,
    bankOptions,
    branchesList,
    selectedBankOption,
    onBankChange,
    onBranchSelect,
    onBranchClear,
    onAccountChange,
    actions,
  } = useBankBranchSelection();
  const selectedBansForUpdate = useSelectedBansForUpdatePayment();
  const allBans = useSelector(selectServerData);
  const epiData = useSelector(selectEpiData);
  const loginData = useSelector((state) => state.authentication.loginData);

  const [accountError, setAccountError] = useState('');
  const [showFormError, setShowFormError] = useState(false);

  useEffect(() => {
    dwhUpdatePaymentMethodReport({
      banData: selectedBanData,
      loginData,
      allBans,
      selectedBanNumbers: selectedBansForUpdate?.map((ban) => ban.banNumber),
      actionType: DwhActionType.StandingOrderAccountPage,
      messageText: DWHMessageText.StandingOrderPageDisplayed,
      status: DWHStatus.Ongoing,
      lastPaymentTypeValidation: epiData?.lastPaymentTypeValidation,
    });
  }, []);

  const getStandingOrderReportData = (overrides = {}) => ({
    banData: selectedBanData,
    loginData,
    allBans,
    selectedBanNumbers: selectedBansForUpdate?.map((ban) => ban.banNumber),
    lastPaymentTypeValidation: epiData?.lastPaymentTypeValidation,
    ...overrides,
  });

  const isBranchDisabled = !bankObligationInfo.bankCode;
  const isAccountDisabled = !bankObligationInfo.branchCode;

  const openUpdateBansDetailsPopup = () => {
    actions.setShowPopup({showPopUp: true, popupType: popupEnum.UPDATE_PAYMENT_BANS_DETAILS});
  };

  const openBankNotInListPopup = () => {
    actions.setShowPopup({showPopUp: true, popupType: popupEnum.BANK_NOT_IN_LIST});
  };

  const validateForm = () => {
    if (!bankObligationInfo.bankCode || !bankObligationInfo.branchCode) {
      setShowFormError(true);
      setAccountError('');
      return false;
    }

    const accountValidationError = validateInput(bankObligationInfo.bankAccountNo, TextInputEnum.bankAccountNo);
    setAccountError(accountValidationError);
    setShowFormError(false);

    return !accountValidationError;
  };

  const handleContinue = () => {
    if (!validateForm()) {
      return;
    }
    dwhUpdatePaymentMethodReport({
      ...getStandingOrderReportData(),
      actionType: DwhActionType.StandingOrderAccountPage,
      messageText: DWHMessageText.StandingOrderAccountPageButtonClicked,
      status: DWHStatus.Ongoing,
      param5Value: bankObligationInfo.bankName,
    });
    actions.validatePaymentMethod({
      PaymentType: 'Bank',
      bankAccountNo: bankObligationInfo.bankAccountNo,
      branchCode: bankObligationInfo.branchCode,
      bankCode: bankObligationInfo.bankCode,
      banNumber: selectedBanData.banNumber,
    });
  };

  useEnterKeyPress(handleContinue);

  return (
    <div className='debtpayment-step bank-branch-selection-step'>
      <UpdatePaymentBanStrip selectedBans={selectedBansForUpdate} onDetailsClick={openUpdateBansDetailsPopup} />

      <div className={`bank-branch-selection-step__title ${isMobile ? 'm-title' : 'l-title'} extra`}>{STEP_TITLE}</div>

      <div className='bank-branch-selection-step__form'>
        <TextInput placeholder='ת.ז.' value={selectedBanData?.idNumber ?? ''} disabled required />

        <ReactSelect
          placeholder='בנק'
          required
          isRtl
          //maxMenuHeight={162}
          options={bankOptions}
          value={selectedBankOption}
          getOptionLabel={(option) => option.value}
          getOptionValue={(option) => option.key}
          onChange={onBankChange}
        />

        <BranchAutocompleteInput
          placeholder='סניף'
          required
          disabled={isBranchDisabled}
          suggestions={branchesList}
          value={bankObligationInfo.branchCode}
          onSelect={onBranchSelect}
          onClear={onBranchClear}
        />

        <TextInput
          placeholder='מספר חשבון'
          value={bankObligationInfo.bankAccountNo ?? ''}
          error={accountError}
          disabled={isAccountDisabled}
          required
          inputMode='numeric'
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            onAccountChange(value);
            setShowFormError(false);
            setAccountError(validateInput(value, TextInputEnum.bankAccountNo, false));
          }}
        />
      </div>

      <div className='bank-branch-selection-step__missing-bank-link'>
        <div type='button' className='btn-link' onClick={openBankNotInListPopup}>
          הבנק שלך לא מופיע ברשימה?
        </div>
      </div>

      <div className='continue-button'>
        {showFormError && <span className='error'>כדי להמשיך יש למלא את כל השדות</span>}
        <BtnSubmit text='המשך' onclick={handleContinue} />
      </div>
    </div>
  );
};

export default BankBranchSelection;
